import {
  Configuration,
  ClaimReservationsApi,
  ClaimFormsApi,
  GetClaimReservationAvailabilityRequest,
  ClaimCenterAvailability,
  MakeClaimReservationRequest,
  ClaimReservationRequest,
  ClaimReservationResponse,
  GetReservationDetailsRequest,
  EditClaimReservationRequest,
  CancelClaimReservationRequest,
  SendVerificationRequest,
  ClaimReservationCommunicationMethod,
  ValidateCodeRequest,
  ClaimReservationValidationResponse,
  ClaimFormCreatedResponse,
  ClaimFormGeoDataResponse,
} from '@interaction-gaming/player-data-service-browser-client'

type APIConfig = {
  apiKey: string
  basePath: string
}

let CLAIM_RESERVATIONS_API: ClaimReservationsApi
let CLAIM_FORMS_API: ClaimFormsApi

const getClaimReservationsApi = () => {
  if (!CLAIM_RESERVATIONS_API) {
    throw new Error('Claim reservations API has yet to be defined')
  }
  return CLAIM_RESERVATIONS_API
}

const getClaimFormsApi = () => {
  if (!CLAIM_FORMS_API) {
    throw new Error('Claim forms API has yet to be defined')
  }
  return CLAIM_FORMS_API
}

const configureApi = (config: APIConfig) => {
  const configuration = new Configuration({
    ...config,
  })
  CLAIM_RESERVATIONS_API = new ClaimReservationsApi(configuration)
  CLAIM_FORMS_API = new ClaimFormsApi(configuration)
}

const getClaimCentersAvailability = async (
  date: string,
  claimCenterIds: string[],
  numberOfTickets: number,
): Promise<ClaimCenterAvailability[]> => {
  const api = getClaimReservationsApi()

  let durationInMinutes = 15
  if (numberOfTickets < 6) {
    durationInMinutes = 15
  } else if (numberOfTickets < 10) {
    durationInMinutes = 30
  } else if (numberOfTickets < 15) {
    durationInMinutes = 45
  } else {
    durationInMinutes = 60
  }

  const request: GetClaimReservationAvailabilityRequest = {
    date,
    claimCenterIds,
    durationInMinutes,
  }

  return (await api.getClaimReservationAvailability(request)).availability
}

const makeClaimReservation = async (
  claimReservationRequest: ClaimReservationRequest,
): Promise<ClaimReservationResponse> => {
  const api = getClaimReservationsApi()
  const request: MakeClaimReservationRequest = {
    claimReservationRequest,
  }

  return await api.makeClaimReservation(request)
}

const editClaimReservation = async (
  claimReservationRequest: ClaimReservationRequest,
  claimReservationId: string,
): Promise<ClaimReservationResponse> => {
  const api = getClaimReservationsApi()
  const request: EditClaimReservationRequest = {
    claimReservationRequest,
    claimReservationId,
  }

  return await api.editClaimReservation(request)
}

const deleteClaimReservation = async (claimReservationId: string): Promise<void> => {
  const api = getClaimReservationsApi()
  const request: CancelClaimReservationRequest = {
    claimReservationId,
  }

  return await api.cancelClaimReservation(request)
}

const getAppointmentById = async (id: string): Promise<ClaimReservationResponse> => {
  const api = getClaimReservationsApi()
  const request: GetReservationDetailsRequest = {
    claimReservationId: id,
  }

  return await api.getReservationDetails(request)
}

const sendVerification = async (
  phone: string,
  email: string,
  verificationMethod: ClaimReservationCommunicationMethod,
): Promise<void> => {
  const api = getClaimReservationsApi()
  const request: SendVerificationRequest = {
    claimReservationVerificationRequest: {
      phone,
      email,
      verificationMethod,
    },
  }

  return await api.sendVerification(request)
}

const validateCode = async (
  phone: string,
  email: string,
  verificationMethod: ClaimReservationCommunicationMethod,
  code: string,
): Promise<ClaimReservationValidationResponse> => {
  const api = getClaimReservationsApi()
  const request: ValidateCodeRequest = {
    claimReservationValidationRequest: {
      phone,
      email,
      verificationMethod,
      code,
    },
  }

  return await api.validateCode(request)
}

const getClaimFormSchema = async () => {
  return await getClaimFormsApi().getClaimFormSchema()
}

const getClaimFormGeoData = async (): Promise<ClaimFormGeoDataResponse> => {
  return await getClaimFormsApi().getClaimFormGeoData()
}

const checkClaimFormExists = async (claimReservationId: string) => {
  return await getClaimReservationsApi().checkClaimFormExists({ claimReservationId })
}

const submitClaimForm = async (
  formData: Record<string, unknown>,
): Promise<ClaimFormCreatedResponse> => {
  return await getClaimFormsApi().submitClaimForm({ requestBody: formData })
}

const submitClaimFormForReservation = async (
  claimReservationId: string,
  formData: Record<string, unknown>,
): Promise<ClaimFormCreatedResponse> => {
  return await getClaimReservationsApi().submitClaimFormForReservation({
    claimReservationId,
    requestBody: formData,
  })
}

export const PlayerDataServiceAPI = {
  configureApi,
  editClaimReservation,
  deleteClaimReservation,
  getAppointmentById,
  getClaimCentersAvailability,
  makeClaimReservation,
  sendVerification,
  validateCode,
  getClaimFormSchema,
  getClaimFormGeoData,
  checkClaimFormExists,
  submitClaimForm,
  submitClaimFormForReservation,
}
